import type { FixtureExport } from '../core/fixtureApi.js';
import type { FixtureNode } from '../core/FixtureNode.js';
import type { ExplorerRuntime, FixtureModules } from '../core/index.js';
import type { ExceptionError, RenderEventType } from '../core/ErrorInfo.js';
import { createFixtureTree } from '../core/FixtureNode.js';
import { ComponentRendering } from '../core/ComponentRenderer.js';

export class EmbeddedMode implements ExplorerRuntime {
	private _root: FixtureNode | undefined;
	private _rendering: ComponentRendering | undefined;
	private _resizeObserver: ResizeObserver | undefined;
	private _errorPosted = false;
	private _originalConsoleError: typeof console.error | undefined;
	private _originalConsoleWarn: typeof console.warn | undefined;

	constructor(
		private readonly _container: HTMLElement,
		private readonly _fixtureId: string,
		loadSourceModule: Promise<FixtureModules>,
	) {
		this._setupErrorCapture();
		loadSourceModule.then(
			modules => {
				this._root = this._buildTree(modules);
				this._render();
			},
			error => {
				const message = error instanceof Error ? (error.stack ?? error.message) : String(error);
				console.error('[EmbeddedMode] Failed to load fixtures:', message);
				this._container.innerHTML = '';
				const el = document.createElement('div');
				el.style.cssText = 'padding:16px;color:#f44;font-size:13px;font-family:monospace;white-space:pre-wrap;';
				el.textContent = `Failed to load fixture:\n${message}`;
				this._container.appendChild(el);
				this._postError({ message: error instanceof Error ? error.message : String(error), stack: error instanceof Error ? error.stack : undefined });
			},
		);
	}

	private _setupErrorCapture(): void {
		// Intercept console.error and forward to parent
		this._originalConsoleError = console.error;
		console.error = (...args: unknown[]) => {
			this._postConsoleEvent('console.error', args.map(String).join(' '));
			this._originalConsoleError!.apply(console, args);
		};

		// Intercept console.warn and forward to parent
		this._originalConsoleWarn = console.warn;
		console.warn = (...args: unknown[]) => {
			this._postConsoleEvent('console.warn', args.map(String).join(' '));
			this._originalConsoleWarn!.apply(console, args);
		};

		window.addEventListener('error', (event) => {
			this._postError({ message: event.message, stack: event.error?.stack });
			this._postConsoleEvent('window.error', event.message, event.error?.stack);
		});
		window.addEventListener('unhandledrejection', (event) => {
			const reason = event.reason;
			const message = reason instanceof Error ? reason.message : String(reason);
			const stack = reason instanceof Error ? reason.stack : undefined;
			this._postError({ message, stack });
			this._postConsoleEvent('window.unhandledrejection', message, stack);
		});
	}

	private _postConsoleEvent(type: RenderEventType, message: string, stack?: string): void {
		window.parent.postMessage({
			type: 'component-explorer:console',
			eventType: type,
			message,
			stack,
		}, '*');
	}

	private _postError(error: ExceptionError): void {
		if (this._errorPosted) return;
		this._errorPosted = true;
		window.parent.postMessage({
			type: 'component-explorer:error',
			fixtureId: this._fixtureId,
			error,
		}, '*');
	}

	updateFixtures(fixtureModules: FixtureModules): void {
		this._root = this._buildTree(fixtureModules);
		this._render();
	}

	bumpVersion(): void {
		this._render();
	}

	private _render(): void {
		this._rendering?.dispose();
		this._rendering = undefined;
		this._resizeObserver?.disconnect();
		this._resizeObserver = undefined;
		this._container.innerHTML = '';

		if (!this._root) return;

		const node = this._findNode(this._root, this._fixtureId);
		if (!node?.component) {
			console.error(`[EmbeddedMode] Fixture not found: ${this._fixtureId}`);
			return;
		}

		const component = { ...node.component, isolation: 'none' as const };
		this._errorPosted = false;

		try {
			const rendering = new ComponentRendering(this._container, component);
			this._rendering = rendering;

			// Catch async errors from the render promise
			rendering.waitForRendering().catch((err) => {
				this._postError({ message: err instanceof Error ? err.message : String(err), stack: err instanceof Error ? err.stack : undefined });
			});
		} catch (err) {
			// Catch sync errors thrown during render
			this._postError({ message: err instanceof Error ? err.message : String(err), stack: err instanceof Error ? err.stack : undefined });
			return;
		}

		const rendering = this._rendering;
		const fixtureId = this._fixtureId;
		const postSize = () => {
			const rect = rendering.renderTarget.getBoundingClientRect();
			window.parent.postMessage({
				type: 'component-explorer:resize',
				fixtureId,
				width: rect.width,
				height: rect.height,
			}, '*');
		};

		this._resizeObserver = new ResizeObserver(postSize);
		this._resizeObserver.observe(rendering.renderTarget);
	}

	private _buildTree(fixtureModules: FixtureModules): FixtureNode {
		const fixtures = new Map<string, FixtureExport>();
		for (const [path, module] of Object.entries(fixtureModules)) {
			const fixture = module.default;
			if (fixture && typeof fixture === 'object') {
				fixtures.set(path, fixture as FixtureExport);
			}
		}
		return createFixtureTree(fixtures);
	}

	private _findNode(node: FixtureNode, id: string): FixtureNode | undefined {
		if (node.id.value === id) return node;
		for (const child of node.children ?? []) {
			const found = this._findNode(child, id);
			if (found) return found;
		}
		return undefined;
	}
}
